import { observer } from 'mobx-react-lite';
import { Outlet, useLocation } from 'react-router-dom';
import { useStores } from '@/hooks/useStores';
import { useWorkAreaRouteRef } from './workAreaRoute';
import { workAreaRouteState } from './workAreaRouteState';
import { AddressState } from './WorkAreaRouteStates';

/**
 * A work area's address, `/acme/platform/api[/-/…]`, at whichever depth matched: resets page-owned
 * dataset state when the work area changes, and answers the one question a page cannot, which work
 * area the address means (I-0656, AC-M19; R-5, F-0250).
 *
 * - The address names a namespace and a slug, so it resolves exactly, and the page renders. The key
 *   form (`/<key>/tracker`) is left as the reader typed it (I-0681, AC-K8): rewriting it to the handle
 *   form would hand the next person who copies it the one form a rename breaks.
 * - An address from before the cutover that is deep enough to land here (`/p/acme~tracker/items/F-1`,
 *   `/work-areas/views/abc`) is answered as `LegacyAddressRedirect` answers it: its first segment is
 *   reserved, so it can never be a real namespace (AC-AD4, AC-AD6).
 * - An unknown app route (`/-/nothing/here`) says there is no page, rather than looking for a work
 *   area in a namespace called `-`.
 * - An unknown route on a work area (`/acme/tracker/-/nope`) is matched by a deeper work-area route,
 *   whose parameters spell `acme/tracker/-/nope`. The work area is read from the address instead,
 *   split at `-` (`parseAddress`), and when it exists the page says it has no such page and leads
 *   to its items — never "no work area" for one that is there.
 * - The router's catch-all renders this too, for an address no route takes — deeper than
 *   `MAX_NAMESPACE_DEPTH`, or a route longer than any declared. It answers exactly as above, so no
 *   address reaches the crash screen, and with no parameters matched it never renders a page.
 * - ⛔ A reference that names no work area the reader can open says so — or, when it is a namespace
 *   the reader has work areas in (`/acme/platform`), lists them. That is an old handle after a rename
 *   (ADR-111: nothing is left at the old handle), a typo, or a work area the reader lost access to.
 * - ⛔ An ambiguous reference renders the candidates and nothing else.
 * - ⛔ **No page is mounted before the work-area list has answered** (I-0656, reopened 2026-09-25).
 *   A page mounted during the load read the remembered selection the moment the list arrived and
 *   wrote it into the address. The resolver's answer decides; a remembered selection never does.
 *
 * Which of these it is, is `workAreaRouteState`'s to decide, and `AddressState` draws it, so the
 * header, the tab and the switcher name the same state this draws (I-0793).
 */
export const WorkAreaRouteBoundary = observer(function WorkAreaRouteBoundary() {
  const matchedRef = useWorkAreaRouteRef();
  const { projectStore } = useStores();
  const location = useLocation();
  const state = workAreaRouteState({
    pathname: location.pathname,
    matchedRef,
    boundary: true,
    projects: projectStore.projects,
    // Answered once the list has arrived, even empty: then every address names nothing.
    answered: projectStore.projects.length > 0 || projectStore.hasWorkAreaAnswer,
  });
  return state.kind === 'page' ? <Outlet key={state.routeRef} /> : <AddressState state={state} />;
});
